import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription, debounceTime, Subject } from 'rxjs';
import { LegalService } from '../../../services/legal.service';
import { LegalArticle, LegalCategory } from '../../../models/legal-article.model';
import { Loading } from '../../layouts/loading/loading';

@Component({
  selector: 'app-loi',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, TranslateModule, TranslatePipe, Loading],
  templateUrl: './loi.html',
  styleUrl: './loi.css',
})
export class LoiComponent implements OnInit, OnDestroy {

  /* ── état ──────────────────────────────────────────────────────── */
  searchQuery    = '';
  activeCategory = '';
  page           = 1;
  totalPages     = 1;
  total          = 0;

  articles: LegalArticle[]  = [];
  categories: LegalCategory[] = [];

  loading      = false;
  loadingCats  = true;
  error        = '';
  searched     = false;        // true dès qu'une requête a été lancée

  private paramsSub?: Subscription;
  private searchSubject = new Subject<void>();
  private searchSub?: Subscription;

  constructor(
    private legalService: LegalService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    // Chargement des catégories
    this.legalService.getCategories().subscribe({
      next: res => { this.categories = res.data; this.loadingCats = false; },
      error: ()  => { this.loadingCats = false; },
    });

    // Lecture des query params (depuis le menu ou les liens)
    this.paramsSub = this.route.queryParams.subscribe(params => {
      this.searchQuery    = params['q']        ?? '';
      this.activeCategory = params['category'] ?? '';
      this.page           = 1;
      this.search();
    });

    // Debounce pour la frappe dans le champ de recherche
    this.searchSub = this.searchSubject.pipe(debounceTime(400)).subscribe(() => {
      this.page = 1;
      this.updateUrl();
    });
  }

  ngOnDestroy(): void {
    this.paramsSub?.unsubscribe();
    this.searchSub?.unsubscribe();
  }

  /* ── actions utilisateur ────────────────────────────────────────── */
  onInput(): void {
    this.searchSubject.next();
  }

  onSubmit(): void {
    this.page = 1;
    this.updateUrl();
  }

  setCategory(key: string): void {
    this.activeCategory = key;
    this.page = 1;
    this.updateUrl();
  }

  loadPage(p: number): void {
    if (p < 1 || p > this.totalPages) return;
    this.page = p;
    this.search();
    window.scrollTo({ top: 300, behavior: 'smooth' });
  }

  clearSearch(): void {
    this.searchQuery    = '';
    this.activeCategory = '';
    this.page           = 1;
    this.updateUrl();
  }

  /* ── navigation ─────────────────────────────────────────────────── */
  private updateUrl(): void {
    const qp: any = {};
    if (this.searchQuery.trim())    qp['q']        = this.searchQuery.trim();
    if (this.activeCategory.trim()) qp['category'] = this.activeCategory.trim();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: qp,
      replaceUrl: true,
    });
  }

  /* ── appel API ──────────────────────────────────────────────────── */
  search(): void {
    this.loading  = true;
    this.error    = '';
    this.searched = true;
    this.legalService
      .search(this.searchQuery, this.activeCategory, this.page)
      .subscribe({
        next: res => {
          this.articles   = res.data;
          this.total      = res.meta?.total      ?? 0;
          this.totalPages = res.meta?.last_page   ?? 1;
          this.loading    = false;
        },
        error: () => {
          this.error   = 'loi_erreur_chargement';
          this.loading = false;
        },
      });
  }

  /* ── helpers affichage ──────────────────────────────────────────── */
  tf(field: { fr: string; en: string; ar: string } | null | undefined): string {
    if (!field) return '';
    const lang = this.translate.getCurrentLang();
    return (field as any)[lang] || field.fr || field.en || '';
  }

  getCategoryLabel(key: string): string {
    const cat = this.categories.find(c => c.key === key);
    if (cat) return this.tf(cat.labels);
    const lang = this.translate.getCurrentLang();
    const fallback: any = {
      loi:        { fr: 'Loi',         en: 'Law',        ar: 'قانون'  },
      decret:     { fr: 'Décret',      en: 'Decree',     ar: 'مرسوم' },
      arrete:     { fr: 'Arrêté',      en: 'Order',      ar: 'قرار'  },
      circulaire: { fr: 'Circulaire',  en: 'Circular',   ar: 'منشور' },
      reglement:  { fr: 'Règlement',   en: 'Regulation', ar: 'نظام'  },
    };
    return fallback[key]?.[lang] || fallback[key]?.fr || key;
  }

  getCategoryColor(key: string): string {
    const map: Record<string, string> = {
      loi:        'cat--loi',
      decret:     'cat--decret',
      arrete:     'cat--arrete',
      circulaire: 'cat--circulaire',
      reglement:  'cat--reglement',
    };
    return map[key] ?? 'cat--default';
  }

  formatDate(d: string): string {
    try { return new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' }); }
    catch { return d; }
  }

  get pages(): number[] {
    const range: number[] = [];
    const delta = 2;
    for (let i = Math.max(1, this.page - delta); i <= Math.min(this.totalPages, this.page + delta); i++) {
      range.push(i);
    }
    return range;
  }
}
